import {
  promiseState,
  useBooleanState,
  useDataStream,
  useNavigator
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useState } from 'react';
import { switchMap } from 'rxjs/operators';
import { viewTexts } from '../../../constants/view-texts';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { projectListOf } from '../../../global-state/database/project-list';
import { companyId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { routes } from '../../../routing/routes';
import { IProjectType } from '../../../types/record/project';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { ProjectListView } from './project-list-view';
import { useProjectListFiltered } from './use-project-list-filtered';

interface Props {
  koumutenId: number;
  companyId: number;
}

export const ProjectList: FC<Props> = memo(
  ({ koumutenId, companyId }: Readonly<Props>) => {
    const navigator = useNavigator();

    const projectList$ = useDataStream<ListWithPromiseState<IProjectType>>(
      listWithPromiseStateInit<IProjectType>(),
      companyId$.pipe(switchMap(projectListOf))
    );

    const [
      createNewProjectDialogIsOpen,
      openCreateNewProjectDialog,
      closeCreateNewProjectDialog
    ] = useBooleanState(false);

    const [
      editProjectDialogIsOpen,
      openEditProjectDialog,
      closeEditProjectDialog
    ] = useBooleanState(false);

    const [
      deleteProjectDialogIsOpen,
      openDeleteProjectDialog,
      closeDeleteProjectDialog
    ] = useBooleanState(false);

    const [
      // dummy comment for prettier to ignore
      projectToEdit,
      setProjectToEdit
    ] = useState<IProjectType | undefined>(undefined);

    const [
      // dummy comment for prettier to ignore
      projectToDelete,
      setProjectToDelete
    ] = useState<IProjectType | undefined>(undefined);

    const projectItemOnClick = useCallback(
      (projectId: number) => {
        navigator(routes.projectRoot(koumutenId, companyId, projectId));
      },
      [navigator, koumutenId, companyId]
    );

    const editProjectButtonOnClick = useCallback(
      (project: IProjectType) => {
        setProjectToEdit(project);
        openEditProjectDialog();
      },
      [setProjectToEdit, openEditProjectDialog]
    );

    const deleteProjectButtonOnClick = useCallback(
      (project: IProjectType) => {
        setProjectToDelete(project);
        openDeleteProjectDialog();
      },
      [setProjectToDelete, openDeleteProjectDialog]
    );

    const {
      searchString,
      onSearchStringChange,
      status,
      setStatus: onStatusChange,
      author,
      setAuthor: onAuthorChange,
      approver,
      setApprover: onApproverChange,
      page,
      onPageChange,
      rowsPerPage,
      setRowsPerPage: onRowsPerPageChange,
      authorOptions,
      approverOptions,
      numRows,
      projectListFilteredCurrentPage
    } = useProjectListFiltered(projectList$);

    useLoadingPage(projectListFilteredCurrentPage?.status === 'pending');

    return (
      <>
        {projectListFilteredCurrentPage === undefined ? (
          undefined
        ) : projectListFilteredCurrentPage.status === promiseState.error ? (
          <NotFoundTemplate message={viewTexts.notFoundMessages.projectList} />
        ) : (
          <ProjectListView
            searchString={searchString}
            onSearchStringChange={onSearchStringChange}
            status={status}
            onStatusChange={onStatusChange}
            author={author}
            onAuthorChange={onAuthorChange}
            approver={approver}
            onApproverChange={onApproverChange}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            numRows={numRows}
            authorOptions={authorOptions}
            approverOptions={approverOptions}
            companyId={companyId}
            projectListFilteredCurrentPage={
              projectListFilteredCurrentPage.value.latest
            }
            projectOnClick={projectItemOnClick}
            createNewProjectOnClick={openCreateNewProjectDialog}
            createNewProjectDialogOpen={createNewProjectDialogIsOpen}
            createNewProjectDialogOnClose={closeCreateNewProjectDialog}
            editProjectOnClick={editProjectButtonOnClick}
            editProjectDialogIsOpen={editProjectDialogIsOpen}
            editProjectDialogOnClose={closeEditProjectDialog}
            projectToEdit={projectToEdit}
            deleteProjectOnClick={deleteProjectButtonOnClick}
            deleteProjectDialogIsOpen={deleteProjectDialogIsOpen}
            deleteProjectDialogOnClose={closeDeleteProjectDialog}
            projectToDelete={projectToDelete}
          />
        )}
      </>
    );
  }
);

ProjectList.displayName = 'ProjectList';
